import { Link, useLocation } from "wouter";
import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import {
  Home,
  Trophy,
  Users,
  Play,
  Calendar,
  LogOut,
  User,
  Settings,
  Sun,
  Moon,
  Monitor,
  Shield,
} from "lucide-react";
import { type ReactNode, useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useMe } from "@/lib/queries";
import { useAuth } from "@/lib/auth";
import { useTheme, type Theme } from "@/lib/theme";
import { getActiveOrgSlug, setActiveOrgSlug } from "@/lib/api";
import { OrgSwitcher } from "./OrgSwitcher";
import { cn } from "@/lib/cn";

type Tab = {
  href: string;
  label: string;
  Icon: typeof Home;
  accent?: boolean;
};
const tabs: Tab[] = [
  { href: "/dashboard", label: "Home", Icon: Home },
  { href: "/dashboard/leaderboards", label: "Ranks", Icon: Trophy },
  { href: "/dashboard/play", label: "Play", Icon: Play, accent: true },
  { href: "/dashboard/players", label: "Roster", Icon: Users },
  { href: "/dashboard/events", label: "Events", Icon: Calendar },
];

export function Layout({ children }: { children: ReactNode }) {
  const [location] = useLocation();
  const { data: me } = useMe();
  const qc = useQueryClient();

  // Keep the active org slug in sync with what /me returned. First org
  // wins by default; once we have an org switcher click, it persists
  // to localStorage. Also persist the default so subsequent loads
  // don't keep re-deriving it (and to make the storage state
  // self-describing for support / debugging).
  //
  // CRITICAL: when activeOrgSlug actually CHANGES (e.g. null → first
  // sign-in defaults; or a fresh PWA install with empty
  // localStorage), invalidate React Query so any in-flight or
  // already-cached queries that fired WITHOUT the X-Org-Slug header
  // refire with the right org context. Otherwise the dashboard's
  // initial useRoster / useGames calls cache empty results and the
  // active-games banner never sees its data.
  useEffect(() => {
    if (!me) return;
    const stored = localStorage.getItem("activeOrgSlug");
    const validStored = stored && me.orgs.some((o) => o.orgSlug === stored);
    const next = validStored ? stored : me.orgs[0]?.orgSlug ?? null;
    const prev = getActiveOrgSlug();
    if (prev === next) return;
    setActiveOrgSlug(next);
    if (next) {
      localStorage.setItem("activeOrgSlug", next);
    } else {
      localStorage.removeItem("activeOrgSlug");
    }
    // Refire every cached query so the org-scoped endpoints land
    // with the new header. Cheap on a fresh app boot; a no-op on
    // subsequent loads since prev === next.
    qc.invalidateQueries();
  }, [me, qc]);

  return (
    <div className="min-h-dvh bg-background">
      <header
        className="sticky top-0 z-40 flex h-14 items-center justify-between border-b border-border bg-card/80 px-4 backdrop-blur"
        style={{ paddingTop: "max(env(safe-area-inset-top), 0px)" }}
      >
        <div className="flex items-center gap-2 min-w-0">
          <Link
            href="/dashboard"
            className="shrink-0"
            aria-label="Cornholio"
          >
            <img
              src="/logo-cornholio.png"
              alt="Cornholio"
              width={216}
              height={38}
              className="h-7 w-auto"
            />
          </Link>
          <OrgSwitcher me={me ?? null} />
        </div>
        <div className="flex items-center gap-2">
          {me?.isPlatformAdmin && (
            <Link
              href="/admin"
              className="hidden sm:inline-flex items-center gap-1.5 h-9 px-2.5 rounded-md text-xs font-display uppercase tracking-widest border border-border text-muted-foreground hover:bg-muted hover:text-foreground"
              title="Platform admin"
            >
              <Shield size={14} />
              Admin
            </Link>
          )}
          {me?.isPlatformAdmin && (
            <Link
              href="/admin"
              className="sm:hidden inline-flex h-9 w-9 items-center justify-center rounded-md border border-border text-muted-foreground hover:bg-muted hover:text-foreground"
              title="Platform admin"
              aria-label="Platform admin"
            >
              <Shield size={16} />
            </Link>
          )}
          <UserMenu />
        </div>
      </header>

      <DesktopSidebar location={location} />
      <main className="sm:pl-60 pb-24 sm:pb-6">{children}</main>

      <MobileBottomNav location={location} />
    </div>
  );
}

function DesktopSidebar({ location }: { location: string }) {
  // Fixed positioning rather than sticky-inside-flex: the latter has
  // browser quirks (sticky's containing block + flex's align-items
  // interactions). Fixed is unambiguous — pinned to the left edge from
  // just below the header (top:14 / 3.5rem) to the bottom of the viewport.
  const settingsActive = isActive(location, "/dashboard/settings");
  return (
    <aside className="hidden sm:flex fixed left-0 top-14 bottom-0 w-60 flex-col border-r border-border bg-card/50 z-30">
      <nav className="flex-1 p-3 space-y-0.5 overflow-y-auto">
        {tabs.map(({ href, label, Icon, accent }) => {
          const active = isActive(location, href);
          return (
            <Link
              key={href}
              href={href}
              className={cn(
                "flex items-center gap-3 h-11 px-3 rounded-md text-sm font-medium transition-colors",
                accent
                  ? "bg-primary text-primary-foreground hover:bg-primary/90 font-display uppercase tracking-wide shadow"
                  : active
                    ? "bg-muted text-foreground"
                    : "text-muted-foreground hover:bg-muted hover:text-foreground",
              )}
            >
              <Icon size={accent ? 20 : 18} strokeWidth={accent ? 2.5 : 2} />
              {label}
            </Link>
          );
        })}
      </nav>
      <div className="p-3 border-t border-border">
        <Link
          href="/dashboard/settings"
          className={cn(
            "flex items-center gap-3 h-11 px-3 rounded-md text-sm font-medium transition-colors",
            settingsActive
              ? "bg-muted text-foreground"
              : "text-muted-foreground hover:bg-muted hover:text-foreground",
          )}
        >
          <Settings size={18} />
          Org settings
        </Link>
      </div>
    </aside>
  );
}

function MobileBottomNav({ location }: { location: string }) {
  return (
    <nav
      className="fixed bottom-0 left-0 right-0 z-40 grid grid-cols-5 border-t border-border bg-card sm:hidden"
      style={{ paddingBottom: "max(env(safe-area-inset-bottom), 0px)" }}
    >
      {tabs.map(({ href, label, Icon, accent }) => {
        const active = isActive(location, href);
        return (
          <Link
            key={href}
            href={href}
            className={cn(
              "flex flex-col items-center justify-center gap-0.5 h-16 text-[10px] font-display uppercase tracking-wider",
              accent
                ? "text-primary"
                : active
                  ? "text-foreground"
                  : "text-muted-foreground",
            )}
          >
            <span
              className={cn(
                "flex h-9 w-9 items-center justify-center rounded-full",
                accent && "bg-primary text-primary-foreground shadow",
                !accent && active && "bg-muted",
              )}
            >
              <Icon size={accent ? 22 : 18} strokeWidth={accent ? 2.5 : 2} />
            </span>
            {label}
          </Link>
        );
      })}
    </nav>
  );
}

// Play tab lights up on its chooser AND on the two destinations it leads
// to (the live-game form and the virtual game). /dashboard is exact-only
// so it doesn't light up while you're inside a deeper page.
function isActive(location: string, href: string): boolean {
  if (href === "/dashboard") {
    return location === href;
  }
  if (href === "/dashboard/play") {
    return (
      location === "/dashboard/play" ||
      location === "/dashboard/games/new" ||
      location === "/dashboard/virtual"
    );
  }
  return location === href || location.startsWith(`${href}/`);
}

function UserMenu() {
  const { me, signOut } = useAuth();
  const [, setLocation] = useLocation();
  if (!me) return null;

  const initials = me.displayName
    .split(" ")
    .map((p) => p[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger asChild>
        <button className="flex h-9 w-9 items-center justify-center rounded-full overflow-hidden border border-border bg-muted text-sm font-display">
          {me.avatarUrl ? (
            <img src={me.avatarUrl} alt={me.displayName} className="h-full w-full object-cover" />
          ) : (
            initials || <User size={16} />
          )}
        </button>
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content
          className="z-50 min-w-[200px] rounded-md border border-border bg-card shadow-lg p-1"
          sideOffset={6}
          align="end"
        >
          <div className="px-2 py-2">
            <div className="text-sm font-medium truncate">{me.displayName}</div>
            <div className="text-xs text-muted-foreground truncate">
              @{me.slug}
            </div>
          </div>
          <DropdownMenu.Separator className="my-1 h-px bg-border" />
          <DropdownMenu.Item
            onSelect={() => setLocation("/dashboard/profile")}
            className="flex items-center gap-2 rounded px-2 py-2 text-sm cursor-pointer outline-none data-[highlighted]:bg-muted"
          >
            <User size={14} /> Your profile
          </DropdownMenu.Item>
          <DropdownMenu.Item
            onSelect={() => setLocation("/dashboard/settings")}
            className="flex items-center gap-2 rounded px-2 py-2 text-sm cursor-pointer outline-none data-[highlighted]:bg-muted"
          >
            <Settings size={14} /> Org settings
          </DropdownMenu.Item>
          <DropdownMenu.Separator className="my-1 h-px bg-border" />
          <ThemeSubmenu />
          <DropdownMenu.Separator className="my-1 h-px bg-border" />
          <DropdownMenu.Item
            onSelect={async () => {
              await signOut();
              setLocation("/");
            }}
            className="flex items-center gap-2 rounded px-2 py-2 text-sm cursor-pointer outline-none data-[highlighted]:bg-muted text-destructive"
          >
            <LogOut size={14} /> Sign out
          </DropdownMenu.Item>
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}

function ThemeSubmenu() {
  const { theme, setTheme } = useTheme();
  const items: { value: Theme; label: string; Icon: typeof Sun }[] = [
    { value: "light", label: "Light", Icon: Sun },
    { value: "dark", label: "Dark", Icon: Moon },
    { value: "system", label: "System", Icon: Monitor },
  ];
  return (
    <>
      <div className="px-2 py-1.5 text-[10px] font-display uppercase tracking-wider text-muted-foreground">
        Theme
      </div>
      <div className="px-1 pb-1 flex gap-1">
        {items.map(({ value, label, Icon }) => (
          <button
            key={value}
            type="button"
            onClick={() => setTheme(value)}
            className={cn(
              "flex-1 h-8 flex items-center justify-center gap-1 rounded text-xs font-medium border",
              theme === value
                ? "border-primary bg-primary/10 text-foreground"
                : "border-border text-muted-foreground hover:bg-muted",
            )}
            title={label}
          >
            <Icon size={12} />
            {label}
          </button>
        ))}
      </div>
    </>
  );
}
